/**
 * Kontrakt asystenta-żarówki (PLAN §8.6) — wspólny dla API i front-endu.
 *
 * Serwer liczy podpowiedzi z faktów i zwraca KLUCZE, nie zdania: tekst powstaje
 * po stronie klienta przez i18n, więc EN nie dostaje polszczyzny z bazy.
 */

/** Widoki, dla których asystent w ogóle pyta serwer. */
export const ASSISTANT_VIEWS = [
  'dashboard',
  'employees',
  'requests',
  'documents',
  'time',
  'leaves',
  'payroll',
  'health',
  'settings',
  'portal.home',
  'portal.documents',
  'portal.time',
  'portal.leaves',
  'portal.health',
  'portal.requests',
] as const

export type AssistantView = (typeof ASSISTANT_VIEWS)[number]

export function isAssistantView(value: unknown): value is AssistantView {
  return typeof value === 'string' && (ASSISTANT_VIEWS as readonly string[]).includes(value)
}

export type HintTone = 'info' | 'warning' | 'danger' | 'success'

export interface AssistantHint {
  /** Klucz i18n: `assistant.hints.<key>` oraz `assistant.hints.<key>Cta`. */
  key: string
  tone: HintTone
  /** Liczba do interpolacji `{{count}}`; 0, gdy podpowiedź niczego nie liczy. */
  count: number
  /** Dodatkowy parametr `{{meta}}` — osoba albo inna gotowa etykieta. */
  meta?: string
  /**
   * Miesiąc „rrrr-mm", którego dotyczy podpowiedź — klient wstawia go do
   * `{{meta}}` w zapisie języka (fala 9, grupa 151), zamiast surowego ISO.
   */
  month?: string
  /** Cel nawigacji przycisku CTA. */
  to: string
}

/**
 * Widok asystenta dla meta trasy portalu — jawna mapa (fala 8, grupa 112).
 *
 * Wcześniej widok sklejał się z klucza nagłówka (`portal.${meta}`), więc zmiana
 * meta „requests" na „portalRequests" dla nagłówka po cichu wyłączyła asystenta
 * na /me/requests. Pasek, podatki i rozwój nie mają reguł (D26) — brak wpisu
 * znaczy „asystent się nie pokazuje". Mapę pilnuje test po drzewie routera.
 */
export const PORTAL_ASSISTANT_VIEW_BY_META: Readonly<Record<string, AssistantView>> = {
  portal: 'portal.home',
  documents: 'portal.documents',
  time: 'portal.time',
  leaves: 'portal.leaves',
  health: 'portal.health',
  portalRequests: 'portal.requests',
}

/**
 * Klucz widoku asystenta dla trasy: `meta` z `handle` trasy plus kontekst
 * aplikacji. Zwraca null, gdy widok nie ma reguł — asystent się nie pokazuje.
 */
export function assistantViewFor(scope: 'app' | 'portal', metaKey: string): AssistantView | null {
  if (scope === 'portal') return PORTAL_ASSISTANT_VIEW_BY_META[metaKey] ?? null
  return isAssistantView(metaKey) && !metaKey.startsWith('portal.') ? metaKey : null
}
